import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import classNames from 'classnames';
import { Dropdown } from '@widget/popups/Dropdown';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import { SafeLink } from '@core/route-context/RouteContext';
// Default values for breadcrumb configuration
const DEFAULT_MAX_ITEMS = 4;
const DEFAULT_ITEMS_ON_LEFT = 1;
const DEFAULT_ITEMS_ON_RIGHT = 2;
const MIN_ITEMS_ON_RIGHT = 2;
/**
 * @export
 * A breadcrumb navigation component with configurable collapsing behavior.
 *
 * @example
 * ```tsx
 * const items = [
 *   { label: 'Home', icon: ['fas', 'home'], link: '/' },
 *   { label: 'Products', link: '/products' },
 *   { label: 'Electronics', link: '/products/electronics' },
 *   { label: 'Laptops', link: '/products/electronics/laptops' },
 *   { label: 'Gaming', link: '/products/electronics/laptops/gaming' },
 * ];
 *
 * <Breadcrumb items={items} maxItems={4} />
 * ```
 *
 * @example
 * ```tsx
 * <Breadcrumb
 *   items={items}
 *   maxItems={5}
 *   itemsOnLeft={2}
 *   itemsOnRight={2}
 *   separator={<span>→</span>}
 *   onItemClick={(item, index) => console.log('Clicked:', item)}
 * />
 * ```
 */
export const Breadcrumb = ({ items, maxItems, itemsOnLeft, itemsOnRight, className, separator, textColor, backgroundColor, activeColor, separatorColor, hoverColor, onItemClick, }) => {
    // Determine effective values based on provided props
    let effectiveMaxItems;
    let effectiveItemsOnLeft;
    let effectiveItemsOnRight;
    if (itemsOnLeft !== undefined && itemsOnRight !== undefined) {
        // Both specified - use them directly, ignore maxItems
        effectiveItemsOnLeft = itemsOnLeft;
        effectiveItemsOnRight = Math.max(MIN_ITEMS_ON_RIGHT, itemsOnRight);
        effectiveMaxItems = effectiveItemsOnLeft + effectiveItemsOnRight;
    }
    else if (maxItems !== undefined && itemsOnLeft !== undefined) {
        // maxItems and itemsOnLeft specified - calculate itemsOnRight
        effectiveMaxItems = maxItems;
        effectiveItemsOnLeft = itemsOnLeft;
        effectiveItemsOnRight = Math.max(MIN_ITEMS_ON_RIGHT, maxItems - itemsOnLeft);
    }
    else if (maxItems !== undefined && itemsOnRight !== undefined) {
        // maxItems and itemsOnRight specified - calculate itemsOnLeft
        effectiveMaxItems = maxItems;
        effectiveItemsOnRight = Math.max(MIN_ITEMS_ON_RIGHT, itemsOnRight);
        effectiveItemsOnLeft = maxItems - effectiveItemsOnRight;
    }
    else if (maxItems !== undefined) {
        // Only maxItems specified - use 1 on left, rest on right
        effectiveMaxItems = maxItems;
        effectiveItemsOnLeft = DEFAULT_ITEMS_ON_LEFT;
        effectiveItemsOnRight = Math.max(MIN_ITEMS_ON_RIGHT, maxItems - DEFAULT_ITEMS_ON_LEFT);
    }
    else {
        // Nothing specified - use all defaults
        effectiveMaxItems = DEFAULT_MAX_ITEMS;
        effectiveItemsOnLeft = DEFAULT_ITEMS_ON_LEFT;
        effectiveItemsOnRight = DEFAULT_ITEMS_ON_RIGHT;
    }
    const { leftItems, rightItems, hiddenItems } = useMemo(() => {
        if (items.length <= effectiveMaxItems) {
            return {
                leftItems: items.slice(0, -2),
                rightItems: items.slice(-2),
                hiddenItems: []
            };
        }
        const leftItems = items.slice(0, effectiveItemsOnLeft);
        const rightItems = items.slice(-effectiveItemsOnRight);
        const middleItems = items.slice(effectiveItemsOnLeft, -effectiveItemsOnRight);
        return {
            leftItems,
            rightItems,
            hiddenItems: middleItems,
        };
    }, [items, effectiveMaxItems, effectiveItemsOnLeft, effectiveItemsOnRight]);
    const shouldShowEllipsis = hiddenItems.length > 0;
    const handleItemClick = (item, index, e) => {
        if (item.onClick) {
            e.preventDefault();
            item.onClick(item);
        }
        onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick(item, index);
    };
    const renderItemContent = (item) => (_jsxs("span", { className: "uxpcore_breadcrumb__item-content", children: [item.icon && (_jsx(FontAwesomeIconWrapper, { icon: item.icon, className: "uxpcore_breadcrumb__item-icon" })), _jsx("span", { className: "uxpcore_breadcrumb__item-label", children: item.label })] }));
    const renderBreadcrumbItem = (item, index, isActive) => {
        const content = renderItemContent(item);
        // Active or non-interactive item
        if (isActive || (!item.link && !item.onClick)) {
            return (_jsx("span", { className: classNames('uxpcore_breadcrumb__item', {
                    'uxpcore_breadcrumb__item--active': isActive,
                }), children: content }));
        }
        // Link item
        if (item.link) {
            return (_jsx(SafeLink, { to: item.link, className: classNames('uxpcore_breadcrumb__item', 'uxpcore_breadcrumb__item--link'), onClick: (e) => handleItemClick(item, index, e), children: content }));
        }
        // Button item
        return (_jsx("button", { type: "button", className: classNames('uxpcore_breadcrumb__item', 'uxpcore_breadcrumb__item--button'), onClick: (e) => handleItemClick(item, index, e), children: content }));
    };
    const renderSeparator = () => {
        return (_jsx("span", { className: "uxpcore_breadcrumb__separator", "aria-hidden": "true", children: separator || _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'chevron-right'] }) }));
    };
    const renderEllipsisDropdown = () => {
        return (_jsx(Dropdown, { trigger: _jsx("button", { type: "button", className: "uxpcore_breadcrumb__ellipsis", "aria-label": "Show hidden breadcrumb items", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'ellipsis-h'] }) }), content: _jsx("div", { className: "uxpcore_breadcrumb__dropdown", children: hiddenItems.map((item, index) => {
                    const actualIndex = effectiveItemsOnLeft + index;
                    return (_jsx("div", { className: "uxpcore_breadcrumb__dropdown-item", children: renderBreadcrumbItem(item, actualIndex, false) }, actualIndex));
                }) }), position: "bottom-left", className: "uxpcore_breadcrumb__ellipsis-dropdown" }));
    };
    // Build custom CSS variables for color customization
    const customStyles = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (textColor && { '--breadcrumb-text-color': textColor })), (backgroundColor && { '--breadcrumb-bg-color': backgroundColor })), (activeColor && { '--breadcrumb-active-color': activeColor })), (separatorColor && { '--breadcrumb-separator-color': separatorColor })), (hoverColor && { '--breadcrumb-hover-color': hoverColor }));
    return (_jsx("nav", { className: classNames('uxpcore_breadcrumb', className), "aria-label": "Breadcrumb", style: Object.keys(customStyles).length > 0 ? customStyles : undefined, children: _jsxs("ol", { className: "uxpcore_breadcrumb__list", children: [leftItems.map((item, index) => (_jsxs("li", { className: "uxpcore_breadcrumb__list-item", children: [renderBreadcrumbItem(item, index, false), renderSeparator()] }, index))), shouldShowEllipsis && (_jsxs("li", { className: "uxpcore_breadcrumb__list-item", children: [renderEllipsisDropdown(), renderSeparator()] })), rightItems.map((item, index) => {
                    const actualIndex = items.length - effectiveItemsOnRight + index;
                    const isLast = actualIndex === items.length - 1;
                    return (_jsxs("li", { className: "uxpcore_breadcrumb__list-item", children: [renderBreadcrumbItem(item, actualIndex, isLast), !isLast && renderSeparator()] }, actualIndex));
                })] }) }));
};
